import React, { useCallback, useMemo } from 'react';
import { useEffect, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import axios from 'axios';
import Swal from 'sweetalert2';
import AlertComponent from '../../../../utility/AlertComponent';
import IAlertType from '../../../../../utils/types/props/IAlertType';
import ClientSelect from '../../../../select/ClientSelect';
import { IClient } from '../../../../../utils/types/IClient';
import apiClient from '../../../../../services/api/apiClient';
import { logger } from '../../../../../services/logger';
import { Carte } from '../../../../../utils/types/Carte';
import { formatAxiosError } from '../../../../../utils/formatAxiosError';
import MultiAllocationCarteGrid from '../../../../grid/MultiAllocationCarteGrid';
import { SoldeClientDTO } from '../../../../../utils/types/objects/SoldeClientDTO';
import { useQuery } from '@tanstack/react-query';
import QueryErrorAlerts, { QueryErrorEntry } from '../../../../others/QueryErrorAlerts';
import formatNumber from '../../../../../utils/formatters/formatNumber';

//#region interfaces

interface IMultiAllocationProps {
    setShowModal?: React.Dispatch<React.SetStateAction<boolean>>;
    trigger?: React.ReactElement<{ onClick?: React.MouseEventHandler }>;
    onUpdate?: () => void;
}

//#endregion

const MultiAllocationModalContent: React.FC<IMultiAllocationProps> = ({ setShowModal, onUpdate }) => {
    const [selectedCardCode, setSelectedCardCode] = useState<string | null>(null);

    // ---

    const {
        data: clients = [],
        isLoading: isLoadingClients,
        isFetching: isFetchingClients,
        refetch: refetchClients,
        isError: isClientsError,
        error: clientsError,
    } = useQuery<IClient[]>({
        queryKey: ['clients'],
        queryFn: async () => {
            const res = await apiClient.get('/api/clients/getClients');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des clients' },
        staleTime: 1000 * 60 * 10, // 10 min
    });

    const {
        data: synthese,
        isError: isSyntheseError,
        error: syntheseError,
        refetch: loadSynthese,
    } = useQuery<SoldeClientDTO | null>({
        queryKey: [
            'synthese-client',
            {
                codeClient: selectedCardCode,
            },
        ],
        queryFn: async () => {
            if (!selectedCardCode) return null;

            const response = await apiClient.get<SoldeClientDTO | null>('/api/clients/get-solde-client', {
                params: {
                    codeClient: selectedCardCode,
                },
            });

            return response.data ?? null;
        },
        meta: { title: 'Chargement de la synthèse client' },
        enabled: Boolean(selectedCardCode),
        staleTime: 0,
    });

    //#region useState

    const [loading, setLoading] = useState({
        cartes: { title: 'Chargement des cartes', isLoading: false },
        submittingOperation: { title: "Enregistrement de l'opération", isLoading: false },
    });

    const [cartes, setCartes] = useState<Carte[]>([]);

    const [systemAlerts, setSystemAlerts] = useState<{ type: IAlertType; message: string }[]>([]);
    const handleDismissAlert = (index: number) => {
        setSystemAlerts((prevAlerts) => prevAlerts.filter((_, i) => i !== index));
    };

    //#endregion

    //#region loadMethods ===

    const fetchCartes = useCallback(async () => {
        setCartes([]);

        if (selectedCardCode) {
            try {
                setLoading((prev: any) => ({
                    ...prev,
                    cartes: {
                        ...prev.cartes,
                        isLoading: true,
                    },
                }));

                const response = await apiClient.get('/api/cartes/byClient', {
                    params: {
                        codeClient: selectedCardCode,
                    },
                });

                setCartes(response.data);
            } catch (err: any) {
                if (!axios.isCancel(err)) {
                    setSystemAlerts((prevAlerts) => [
                        ...prevAlerts,
                        {
                            type: 'danger',
                            message: 'Erreur lors du chargement des cartes. ' + formatAxiosError(err)?.message,
                        },
                    ]);
                }

                logger.error(err);

                setCartes([]);
            } finally {
                setLoading((prev: any) => ({
                    ...prev,
                    cartes: {
                        ...prev.cartes,
                        isLoading: false,
                    },
                }));
            }
        }
    }, [selectedCardCode]);

    //#endregion

    //#region methods

    const handleLoadSyntheseClient = useCallback(async () => {
        if (!selectedCardCode) return;
        loadSynthese();
    }, [selectedCardCode, loadSynthese]);

    const handleCarteUpdated = (updatedCarte: Carte) => {
        setCartes((prev) => {
            const newCartes = prev.map((c) => (c.id === updatedCarte.id ? { ...updatedCarte } : c));
            return newCartes;
        });
    };

    const submitForm = async () => {
        const allocations = cartes.reduce((acc: any, c) => {
            if ((c.idArticle != null && c.allocationQte != null && c.allocationQte > 0) || (c.idArticle == null && c.allocationValeur != null && c.allocationValeur > 0)) {
                acc[c.numero ?? ''] = {
                    ...acc[c.numero ?? ''],
                    ...(c.idArticle !== null ? { quantity: c.allocationQte ?? 0 } : { value: c.allocationValeur ?? 0 }),
                };
            }
            return acc;
        }, {});

        if (!selectedCardCode) {
            Swal.fire({
                text: 'Client incorrect.',
                icon: 'error',
            });

            return;
        }

        const payload = cartes
            .map((carte) => {
                const allocation = allocations[carte.numero ?? ''] || {};
                const hasValidAllocation = (carte.idArticle !== null && (allocation.quantity ?? 0) > 0) || (carte.idArticle === null && (allocation.value ?? 0) > 0);

                return hasValidAllocation
                    ? {
                          carteId: carte.id,
                          codeClient: selectedCardCode,
                          ...(carte.idArticle !== null && { allocationQuantite: allocation.quantity }),
                          ...(carte.idArticle === null && { allocationValeur: allocation.value }),
                      }
                    : null;
            })
            .filter(Boolean);

        if (payload.length === 0) {
            Swal.fire({
                text: 'Veuillez saisir au moins une recharge valide (> 0).',
                icon: 'warning',
            });

            return;
        }

        try {
            setLoading((prev: any) => ({
                ...prev,
                submittingOperation: {
                    ...prev.submittingOperation,
                    isLoading: true,
                },
            }));

            const response = await apiClient.post('/api/cartes/recharges', {
                cardCode: selectedCardCode,
                recharges: payload,
            });

            if (response.data.success === true) {
                Swal.fire({
                    text: 'Opération effectuée avec succès.',
                    icon: 'success',
                    confirmButtonText: 'OK',
                }).then(() => {
                    setShowModal?.(false);
                    onUpdate?.();
                });
            } else {
                setSystemAlerts((prevAlerts) => [
                    ...prevAlerts,
                    {
                        type: 'danger',
                        message: "Erreur lors l'enregistrement de l'opération. " + response.data.message,
                    },
                ]);
            }
        } catch (err) {
            Swal.fire({
                text: formatAxiosError(err)?.message ?? 'Erreur lors de la recharge des cartes.',
                icon: 'error',
            });
        } finally {
            setLoading((prev: any) => ({
                ...prev,
                submittingOperation: {
                    ...prev.submittingOperation,
                    isLoading: false,
                },
            }));
        }
    };

    //#endregion

    //#region useEffect ===

    useEffect(() => {
        fetchCartes();
    }, [fetchCartes]);

    useEffect(() => {
        if (clients.length === 1) {
            setSelectedCardCode(clients[0].cardCode);
        }
    }, [clients]);

    //#endregion ===

    // ---

    const queryErrors = useMemo<QueryErrorEntry[]>(
        () => [
            { isError: isClientsError, error: clientsError, title: 'Erreur lors du chargement des clients', refetch: refetchClients },
            { isError: isSyntheseError, error: syntheseError, title: 'Erreur lors du chargement de la synthèse client', refetch: handleLoadSyntheseClient },
        ],
        [isClientsError, clientsError, refetchClients, isSyntheseError, syntheseError, handleLoadSyntheseClient],
    );

    // ---

    return (
        <>
            <AlertComponent alerts={systemAlerts} onDismiss={handleDismissAlert} />
            <QueryErrorAlerts queries={queryErrors} />

            <div>
                <form action="#">
                    <div className="row">
                        <div className="col-md-6 form-group">
                            <label htmlFor="client-select">Client</label>
                            <ClientSelect id="client-select" clients={clients} isClientsLoading={isLoadingClients || isFetchingClients} selectedCardCode={selectedCardCode} setSelectedCardCode={setSelectedCardCode} />
                        </div>

                        {selectedCardCode && (
                            <div className="col-md-2 form-group">
                                <label htmlFor="solde">Solde client</label>
                                <input id="solde" type="text" className="form-control text-end" value={formatNumber(synthese?.solde ?? 0)} readOnly />
                            </div>
                        )}
                    </div>

                    <div className="mt-2">
                        <MultiAllocationCarteGrid cartes={cartes} handleCarteUpdated={handleCarteUpdated} />
                    </div>

                    <div className="text-end mt-3">
                        <button
                            type="button"
                            className="btn btn-success"
                            onClick={() => {
                                submitForm();
                            }}
                            disabled={cartes.length === 0 || loading.submittingOperation.isLoading}
                        >
                            {loading.submittingOperation.isLoading ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                    &nbsp; Valider
                                </>
                            ) : (
                                `Valider`
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

const MultiAllocationModal: React.FC<IMultiAllocationProps> = ({ onUpdate, trigger }) => {
    const [showModal, setShowModal] = useState(false);

    const handleOpenModal = () => setShowModal(true);
    const handleCloseModal = () => setShowModal(false);

    // Clone element si trigger custom veut gérer le click
    const renderTrigger = () => {
        if (trigger) {
            return React.cloneElement(trigger, {
                onClick: (e: React.MouseEvent) => {
                    trigger.props.onClick?.(e);
                    handleOpenModal();
                },
            });
        }

        return (
            <button className="btn btn-1" onClick={handleOpenModal}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-reload">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M19.933 13.041a8 8 0 1 1 -9.925 -8.788c3.899 -1 7.935 1.007 9.425 4.747" />
                    <path d="M20 4v5h-5" />
                </svg>
                Recharge cartes
            </button>
        );
    };

    return (
        <div>
            {renderTrigger()}

            <Modal show={showModal} onHide={handleCloseModal} className="fade" centered scrollable backdrop="static" size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Recharge</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <MultiAllocationModalContent setShowModal={setShowModal} onUpdate={onUpdate} />
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default MultiAllocationModal;
